import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff, User, Lock, Upload } from "lucide-react";
import {
  fetchProvince,
  fetchDistrict,
  fetchLocalGovernments,
  fetchOrgInfo,
  createUserSignUp,
  fetchCheckEmail,
  fetchOTP,
  fetchChangePassword,
} from "../../services/api";
import {
  USER_TYPES,
  getAvailableUserTypesForSignup,
  isUserTypeAllowed,
  getPlatformUserTypeRestrictions,
} from "../../utils/platformUtils";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// import logo from "../../../assets/images/logo-login.png";
import LogoNRCS from "../../../assets/images/LogoNRCS.png";

function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login form state
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Signup form state (expanded)
  const [signupData, setSignupData] = useState({
    name: "",
    gender: "",
    mobile: "",
    email: "",
    birthday: "1990-01-01",
    password: "",
    confirmPassword: "",
    userType: getAvailableUserTypesForSignup()[0].value,
    organization: null,
    province: null,
    district: null,
    localGvt: null,
    wardNo: "",
    tole: "",
    letterFile: null,
    letterBase64: null,
    // Organization Information fields
    orgname: "",
    orgphone: "",
    orgemail: "",
    orgregdate: "",
  });
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [localGvtOptions, setLocalGvtOptions] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [uploadingLetter, setUploadingLetter] = useState(false);

  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Forgot Password state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    otp: ["", "", "", ""], // Array for 4 OTP digits
    newPassword: "",
    confirmPassword: "",
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [otpRefs, setOtpRefs] = useState([]);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Fetch organizations on mount (web only)
  useEffect(() => {
    if (!isLogin) {
      setOrganizationLoading(true);
      fetchOrgInfo()
        .then((orgData) => {
          let organizations = [];
          if (orgData && Array.isArray(orgData)) {
            organizations = orgData;
          } else if (
            orgData &&
            orgData.organizations &&
            Array.isArray(orgData.organizations)
          ) {
            organizations = orgData.organizations;
          } else if (orgData && orgData.data && Array.isArray(orgData.data)) {
            organizations = orgData.data;
          }
          const formattedOrganizations = organizations
            .map((org) => ({
              id: org.id || org.ad_org_id || org.AD_Org_ID,
              name:
                org.name ||
                org.Name ||
                org.identifier ||
                "Unknown Organization",
              ad_org_id: org.id || org.ad_org_id || org.AD_Org_ID,
              value: org.value || org.Value || org.name || org.Name,
              description: org.description || org.Description || "",
              isActive: org.isActive || org.IsActive || true,
            }))
            .filter((org) => org.id && org.name);
          setOrganizationOptions(
            formattedOrganizations.sort((a, b) => a.name.localeCompare(b.name))
          );
        })
        .catch((err) =>
          setError("Failed to load organizations. Please try again.")
        )
        .finally(() => setOrganizationLoading(false));
    }
  }, [isLogin]);

  // Fetch provinces on mount
  useEffect(() => {
    if (!isLogin) {
      setLocationLoading(true);
      fetchProvince(258)
        .then((provinces) => {
          setProvinceOptions(
            provinces.sort((a, b) => a.name.localeCompare(b.name))
          );
        })
        .catch(() => setError("Failed to load provinces. Please try again."))
        .finally(() => setLocationLoading(false));
    }
  }, [isLogin]);

  // Fetch districts when province changes
  useEffect(() => {
    if (!isLogin && signupData.province) {
      setLocationLoading(true);
      fetchDistrict(signupData.province.id)
        .then((districts) => {
          setDistrictOptions(
            districts.sort((a, b) => a.name.localeCompare(b.name))
          );
        })
        .catch(() => setError("Failed to load districts. Please try again."))
        .finally(() => setLocationLoading(false));
    }
  }, [isLogin, signupData.province]);

  // Fetch local governments when district changes
  useEffect(() => {
    if (!isLogin && signupData.district) {
      setLocationLoading(true);
      fetchLocalGovernments(signupData.district.id)
        .then((localGovs) => {
          setLocalGvtOptions(
            localGovs.sort((a, b) => a.name.localeCompare(b.name))
          );
        })
        .catch(() =>
          setError("Failed to load local governments. Please try again.")
        )
        .finally(() => setLocationLoading(false));
    }
  }, [isLogin, signupData.district]);

  // Letter upload handler
  const handleLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLetter(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignupData((prev) => ({
          ...prev,
          letterFile: file,
          letterBase64: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError("Failed to upload letter. Please try again.");
    } finally {
      setUploadingLetter(false);
    }
  };

  // Signup form input handler
  const handleSignupInputChange = (field, value) => {
    setSignupData((prev) => ({ ...prev, [field]: value }));
    // Reset dependent dropdowns
    if (field === "province") {
      setSignupData((prev) => ({ ...prev, district: null, localGvt: null }));
      setDistrictOptions([]);
      setLocalGvtOptions([]);
    }
    if (field === "district") {
      setSignupData((prev) => ({ ...prev, localGvt: null }));
      setLocalGvtOptions([]);
    }
    if (field === "userType") {
      setSignupData((prev) => ({
        ...prev,
        organization: null,
        letterFile: null,
        letterBase64: null,
      }));
    }
  };

  // Signup submit handler
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    // Validation
    if (
      !signupData.name ||
      !signupData.mobile ||
      !signupData.email ||
      !signupData.birthday ||
      !signupData.password ||
      !signupData.confirmPassword ||
      !signupData.gender ||
      !signupData.orgname ||
      !signupData.orgphone ||
      !signupData.orgemail ||
      !signupData.orgregdate
    ) {
      setError("Please fill all fields.");
      setLoading(false);
      return;
    }
    if (
      (signupData.userType === USER_TYPES.HOSPITAL ||
        signupData.userType === USER_TYPES.BDO) &&
      !signupData.organization
    ) {
      setError("Please select an organization.");
      setLoading(false);
      return;
    }
    if (signupData.password !== signupData.confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    if (signupData.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }
    if (!isUserTypeAllowed(signupData.userType)) {
      setError(getPlatformUserTypeRestrictions().message);
      setLoading(false);
      return;
    }
    if (
      (signupData.userType === USER_TYPES.HOSPITAL ||
        signupData.userType === USER_TYPES.BDO) &&
      !signupData.letterBase64
    ) {
      setError("Please upload a letter/document for verification.");
      setLoading(false);
      return;
    }
    // Build payload
    const userPayload = {
      name: signupData.name,
      birthday: signupData.birthday,
      gender: signupData.gender,
      email: signupData.email,
      isvendor: "N",
      isdonor: "N",
      iscustomer: "Y",
      phone: signupData.mobile,
      password: signupData.password,
      // Organization Information
      orgname: signupData.orgname,
      orgphone: signupData.orgphone,
      orgemail: signupData.orgemail,
      orgregdate: signupData.orgregdate,
      address: {
        C_Country_ID: 258,
        n_province_ID: signupData.province?.id,
        n_district_ID: signupData.district?.id,
        n_locgov_ID: signupData.localGvt?.id,
        ward_no: signupData.wardNo,
        tole: signupData.tole,
      },
    };
    if (signupData.organization?.id) {
      userPayload.ad_org_id = signupData.organization.id;
    }
    if (signupData.letterBase64) {
      userPayload.letter = {
        image: signupData.letterBase64,
        filename: `letter_${Date.now()}.jpg`,
      };
    }
    if (signupData.userType === USER_TYPES.BDO) {
      userPayload.isbdo = true;
    }
    if (signupData.userType === USER_TYPES.HOSPITAL) {
      userPayload.isHospital = true;
    }
    if (signupData.userType === USER_TYPES.PUBLIC_USER) {
      userPayload.isdonor = "Y";
      userPayload.iscustomer = "Y";
    }
    try {
      await createUserSignUp(userPayload);
      // console.log(`User signed up successfully:`, userPayload);

      setShowSuccessDialog(true);
      setLoading(false);
    } catch (err) {
      console.error("Signup error details:", err);
      const msg = err.message || "Signup failed. Please try again.";
      // Always display the actual server error message
      setError(msg);
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(loginData.username, loginData.password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (formType, field, value) => {
    if (formType === "login") {
      setLoginData((prev) => ({ ...prev, [field]: value }));
    } else {
      setSignupData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setLoading(false);
  };

  // Forgot Password handlers
  const showAlert = (type, message) => {
    setForgotPasswordError(message);
    if (type === "Error") {
      throw new Error(message); // Throw error to prevent progression
    }
  };

  const handleForgotPasswordInputChange = (field, value) => {
    if (field === "otp") {
      // Handle OTP array updates
      setForgotPasswordData((prev) => ({ ...prev, [field]: value }));
    } else {
      setForgotPasswordData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Handle OTP input changes for individual boxes
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...forgotPasswordData.otp];
    newOtp[index] = value;
    setForgotPasswordData((prev) => ({ ...prev, otp: newOtp }));

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !forgotPasswordData.otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError("");
    setForgotPasswordLoading(true);

    try {
      if (forgotPasswordStep === 1) {
        // Step 1: Check email and send OTP
        if (!forgotPasswordData.email) {
          setForgotPasswordError("Please enter your email address");
          setForgotPasswordLoading(false);
          return;
        }
        await fetchCheckEmail(forgotPasswordData.email, showAlert);
        setForgotPasswordStep(2);
      } else if (forgotPasswordStep === 2) {
        // Step 2: Verify OTP
        const fullOtp = forgotPasswordData.otp.join("");
        if (!fullOtp || fullOtp.length !== 4) {
          setForgotPasswordError("Please enter the complete 4-digit OTP");
          setForgotPasswordLoading(false);
          return;
        }
        try {
          await fetchOTP(forgotPasswordData.email, fullOtp, showAlert);
          // Only proceed to step 3 if OTP verification was successful
          setForgotPasswordStep(3);
        } catch (otpError) {
          // Error already set by showAlert, don't proceed to next step
          setForgotPasswordLoading(false);
          return;
        }
      } else if (forgotPasswordStep === 3) {
        // Step 3: Change password
        if (
          !forgotPasswordData.newPassword ||
          !forgotPasswordData.confirmPassword
        ) {
          setForgotPasswordError("Please fill in both password fields");
          setForgotPasswordLoading(false);
          return;
        }
        if (
          forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword
        ) {
          setForgotPasswordError("Passwords do not match");
          setForgotPasswordLoading(false);
          return;
        }
        if (forgotPasswordData.newPassword.length < 8) {
          setForgotPasswordError("Password must be at least 8 characters long");
          setForgotPasswordLoading(false);
          return;
        }
        await fetchChangePassword(
          forgotPasswordData.email,
          forgotPasswordData.newPassword,
          forgotPasswordData.confirmPassword,
          showAlert
        );
        // Success - close modal and reset
        setShowForgotPasswordModal(false);
        setForgotPasswordStep(1);
        setForgotPasswordData({
          email: "",
          otp: ["", "", "", ""],
          newPassword: "",
          confirmPassword: "",
        });
        setError(
          "Password changed successfully! Please login with your new password."
        );
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setForgotPasswordError(
        error.message || "An error occurred. Please try again."
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      email: "",
      otp: ["", "", "", ""],
      newPassword: "",
      confirmPassword: "",
    });
    setForgotPasswordError("");
  };

  // Helper for react-select options
  const genderOptions = [
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
    { value: "OT", label: "Others" },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Branding (fixed on md+) */}
      <div className="hidden md:flex md:w-1/2 h-screen fixed left-0 top-0 bg-gradient-to-br from-pink-50 to-white z-10">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-8 h-8 bg-pink-400 rounded-lg transform rotate-45"></div>
          <div className="absolute top-40 right-32 w-6 h-6 bg-pink-300 rounded-lg transform rotate-45"></div>
          <div className="absolute bottom-32 left-32 w-10 h-10 bg-pink-500 rounded-lg transform rotate-45"></div>
          <div className="absolute bottom-20 right-20 w-4 h-4 bg-pink-400 rounded-lg transform rotate-45"></div>
        </div>
        {/* Centered Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full h-full text-center">
          {/* Large Logos */}
          <div className="mb-6 flex items-center justify-center">
            <img
              src={LogoNRCS}
              alt="BTSM Logo"
              className="h-16 md:h-24 w-auto"
            />
          </div>
          {/* Brand Text */}
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
              Blood Transfusion Service Management
            </h3>
            {/* <p className="text-base md:text-xl text-gray-600 leading-relaxed max-w-xs md:max-w-sm mx-auto">
              Blood Transfusion Service Management
            </p> */}
          </div>
        </div>
      </div>

      {/* Right Side - Forms (scrollable) */}
      <div
        className={`flex-1 flex ${
          isLogin ? "items-center justify-center" : "justify-center"
        } bg-white px-4 py-8 sm:px-6 md:px-8 md:py-12 min-h-screen md:ml-auto md:pl-[50vw] md:relative overflow-y-auto h-screen pt-8 md:pt-12`}
        style={{ marginLeft: undefined }}
      >
        <div className="w-full max-w-sm sm:max-w-md md:max-w-md">
          {/* Form Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {isLogin
                ? "Hospital & BDO Web Portal"
                : "Hospital & BDO Web Portal"}
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              {isLogin ? "Sign in to your account" : "Register your account"}
            </p>
            <div className="text-xs text-gray-500 italic mt-1 mb-6">
              <p>
                {isLogin
                  ? ""
                  : "All fields marked with an asterisk (*) are mandatory and must be filled out before submission."}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Login Form */}
          {isLogin && (
            <form onSubmit={handleLoginSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={loginData.username}
                    onChange={(e) =>
                      handleInputChange("login", "username", e.target.value)
                    }
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Enter Email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    required
                    value={loginData.password}
                    onChange={(e) =>
                      handleInputChange("login", "password", e.target.value)
                    }
                    className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                  >
                    {showLoginPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>
              <div className="text-center mt-4">
                <span className="text-gray-600 text-sm">
                  Don't have an account?
                </span>
                <button
                  type="button"
                  onClick={toggleMode}
                  className="ml-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Sign Up
                </button>
              </div>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          )}

          {/* Signup Form */}
          {!isLogin && (
            <form onSubmit={handleSignupSubmit} className="space-y-6">
              {/* Organization Information Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                  Organization Information
                </h3>

                {/* User Type (web only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Register As *
                  </label>
                  <div className="flex flex-col gap-2">
                    {getAvailableUserTypesForSignup().map((type) => (
                      <label
                        key={type.value}
                        className="flex items-center gap-2"
                      >
                        <input
                          type="radio"
                          name="userType"
                          value={type.value}
                          checked={signupData.userType === type.value}
                          onChange={() =>
                            handleSignupInputChange("userType", type.value)
                          }
                          disabled={
                            loading ||
                            getAvailableUserTypesForSignup().length === 1
                          }
                        />
                        {type.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-6 mb-4"></div>

                <div className="space-y-4">
                  {/* Organization Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={signupData.orgname}
                      onChange={(e) =>
                        handleSignupInputChange("orgname", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter organization name"
                    />
                  </div>
                  {/* Organization Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization Phone No *
                    </label>
                    <input
                      type="text"
                      required
                      value={signupData.orgphone}
                      onChange={(e) =>
                        handleSignupInputChange("orgphone", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter organization phone number"
                    />
                  </div>
                  {/* Organization Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Organization Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={signupData.orgemail}
                      onChange={(e) =>
                        handleSignupInputChange("orgemail", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter organization email"
                    />
                  </div>
                  {/* Date of Registration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Registration *
                    </label>
                    <DatePicker
                      selected={
                        signupData.orgregdate
                          ? new Date(signupData.orgregdate)
                          : null
                      }
                      onChange={(date) => {
                        // Format as YYYY-MM-DD
                        const yyyy = date?.getFullYear();
                        const mm = String(date?.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const dd = String(date?.getDate()).padStart(2, "0");
                        const formatted = date ? `${yyyy}-${mm}-${dd}` : "";
                        handleSignupInputChange("orgregdate", formatted);
                      }}
                      dateFormat="yyyy-MM-dd"
                      maxDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select date of registration"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      disabled={loading}
                    />
                  </div>

                  {/* Address Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Province *
                    </label>
                    <Select
                      options={provinceOptions.map((prov) => ({
                        value: prov.id,
                        label: prov.name,
                      }))}
                      value={
                        signupData.province
                          ? {
                              value: signupData.province.id,
                              label: signupData.province.name,
                            }
                          : null
                      }
                      onChange={(option) => {
                        const selected = provinceOptions.find(
                          (opt) => opt.id === option?.value
                        );
                        handleSignupInputChange("province", selected || null);
                      }}
                      isDisabled={loading || locationLoading}
                      placeholder="Select Province"
                      classNamePrefix="react-select"
                    />
                  </div>
                  {districtOptions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        District
                      </label>
                      <Select
                        options={districtOptions.map((dist) => ({
                          value: dist.id,
                          label: dist.name,
                        }))}
                        value={
                          signupData.district
                            ? {
                                value: signupData.district.id,
                                label: signupData.district.name,
                              }
                            : null
                        }
                        onChange={(option) => {
                          const selected = districtOptions.find(
                            (opt) => opt.id === option?.value
                          );
                          handleSignupInputChange("district", selected || null);
                        }}
                        isDisabled={
                          loading || locationLoading || !signupData.province
                        }
                        placeholder="Select District"
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}
                  {localGvtOptions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Local Government
                      </label>
                      <Select
                        options={localGvtOptions.map((lg) => ({
                          value: lg.id,
                          label: lg.name,
                        }))}
                        value={
                          signupData.localGvt
                            ? {
                                value: signupData.localGvt.id,
                                label: signupData.localGvt.name,
                              }
                            : null
                        }
                        onChange={(option) => {
                          const selected = localGvtOptions.find(
                            (opt) => opt.id === option?.value
                          );
                          handleSignupInputChange("localGvt", selected || null);
                        }}
                        isDisabled={
                          loading || locationLoading || !signupData.district
                        }
                        placeholder="Select Local Government"
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ward No *
                    </label>
                    <input
                      type="text"
                      value={signupData.wardNo}
                      onChange={(e) =>
                        handleSignupInputChange("wardNo", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter ward number"
                      disabled={loading || locationLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tole *
                    </label>
                    <input
                      type="text"
                      value={signupData.tole}
                      onChange={(e) =>
                        handleSignupInputChange("tole", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter tole"
                      disabled={loading || locationLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Approving Organization (web only, Hospital/BDO) */}
              {(signupData.userType === USER_TYPES.HOSPITAL ||
                signupData.userType === USER_TYPES.BDO) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approving Chapter *
                  </label>
                  <Select
                    options={organizationOptions.map((org) => ({
                      value: org.id,
                      label: org.name,
                    }))}
                    value={
                      signupData.organization
                        ? {
                            value: signupData.organization.id,
                            label: signupData.organization.name,
                          }
                        : null
                    }
                    onChange={(option) => {
                      const selected = organizationOptions.find(
                        (opt) => opt.id === option?.value
                      );
                      handleSignupInputChange("organization", selected || null);
                    }}
                    isDisabled={loading || organizationLoading}
                    placeholder="Select Chapter"
                    classNamePrefix="react-select"
                  />
                </div>
              )}

              {/* Request Letter (web only, Hospital/BDO) */}
              {(signupData.userType === USER_TYPES.HOSPITAL ||
                signupData.userType === USER_TYPES.BDO) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Letter *
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="letter-upload-input"
                      type="file"
                      accept=".jpeg,.jpg,.png"
                      onChange={handleLetterUpload}
                      disabled={loading || uploadingLetter}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm font-medium transition-colors border focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2
                            ${
                              signupData.letterFile
                                ? "bg-green-600 text-white hover:bg-green-700 border-green-700"
                                : "bg-gray-100 text-gray-800 hover:bg-primary-600 hover:text-white border-gray-300"
                            }
                            disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={loading || uploadingLetter}
                      onClick={() =>
                        document.getElementById("letter-upload-input").click()
                      }
                    >
                      <Upload className="h-5 w-5" />
                      {signupData.letterFile
                        ? "Change Letter"
                        : uploadingLetter
                        ? "Uploading..."
                        : "Upload Request Letter"}
                    </button>
                    {signupData.letterFile && (
                      <>
                        <span
                          className="text-green-600 text-sm truncate max-w-[120px]"
                          title={signupData.letterFile.name}
                        >
                          {signupData.letterFile.name}
                        </span>
                        <button
                          type="button"
                          className="text-red-600 text-xs underline ml-2"
                          onClick={() =>
                            handleSignupInputChange("letterFile", null) ||
                            handleSignupInputChange("letterBase64", null)
                          }
                          disabled={loading || uploadingLetter}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 italic mt-1 mb-2">
                    Please upload a clear photo of request letter/document.
                  </div>
                </div>
              )}

              {/* Contact Person Details Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                  Contact Person Details
                </h3>
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={signupData.name}
                      onChange={(e) =>
                        handleSignupInputChange("name", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter full name"
                    />
                  </div>
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender *
                    </label>
                    <Select
                      options={genderOptions}
                      value={
                        genderOptions.find(
                          (g) => g.value === signupData.gender
                        ) || null
                      }
                      onChange={(option) =>
                        handleSignupInputChange(
                          "gender",
                          option ? option.value : ""
                        )
                      }
                      isDisabled={loading}
                      placeholder="Select Gender"
                      classNamePrefix="react-select"
                    />
                  </div>
                  {/* Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number *
                    </label>
                    <input
                      type="text"
                      required
                      value={signupData.mobile}
                      onChange={(e) =>
                        handleSignupInputChange("mobile", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter mobile number"
                    />
                  </div>
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Official Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={signupData.email}
                      onChange={(e) =>
                        handleSignupInputChange("email", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Enter official email"
                    />
                  </div>
                  {/* Birthday */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth *
                    </label>
                    <DatePicker
                      selected={
                        signupData.birthday
                          ? new Date(signupData.birthday)
                          : null
                      }
                      onChange={(date) => {
                        // Format as YYYY-MM-DD
                        const yyyy = date?.getFullYear();
                        const mm = String(date?.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const dd = String(date?.getDate()).padStart(2, "0");
                        const formatted = date ? `${yyyy}-${mm}-${dd}` : "";
                        handleSignupInputChange("birthday", formatted);
                      }}
                      dateFormat="yyyy-MM-dd"
                      maxDate={new Date()}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select date of birth"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      disabled={loading}
                    />
                  </div>
                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showSignupPassword ? "text" : "password"}
                        required
                        value={signupData.password}
                        onChange={(e) =>
                          handleSignupInputChange("password", e.target.value)
                        }
                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        placeholder="8+ characters"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        onClick={() => setShowSignupPassword((v) => !v)}
                      >
                        {showSignupPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        value={signupData.confirmPassword}
                        onChange={(e) =>
                          handleSignupInputChange(
                            "confirmPassword",
                            e.target.value
                          )
                        }
                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Error message placeholder - this section has been moved to Organization Information */}
              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  "Sign Up"
                )}
              </button>
              <div className="text-center mt-4">
                <span className="text-gray-600 text-sm">
                  Already have an account?
                </span>
                <button
                  type="button"
                  onClick={toggleMode}
                  className="ml-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                >
                  Sign In
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="text-center mt-8 pb-4 md:pb-0">
            <p className="text-xs text-gray-500">
              © NRCS. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Success Message Modal */}
      {showSuccessDialog &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h2 className="text-xl font-semibold mb-2">
                Registration Successful!
              </h2>
              <p className="text-gray-600 mb-4">
                Your request has been received and is under verification. Once
                verified, you will receive an email regarding your account
                activation.
              </p>
              <button
                onClick={() => {
                  setShowSuccessDialog(false);
                  setIsLogin(true);
                }}
                className="btn-primary w-full"
              >
                Go to Login
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Forgot Password Modal */}
      {showForgotPasswordModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {forgotPasswordStep === 1 && "Reset Password"}
                  {forgotPasswordStep === 2 && "Verify OTP"}
                  {forgotPasswordStep === 3 && "Set New Password"}
                </h2>
                <button
                  onClick={closeForgotPasswordModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Error Message */}
              {forgotPasswordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {forgotPasswordError}
                </div>
              )}

              <form onSubmit={handleForgotPasswordSubmit}>
                {/* Step 1: Email Input */}
                {forgotPasswordStep === 1 && (
                  <div>
                    <p className="text-gray-600 mb-4">
                      Enter your email address and we'll send you an OTP to
                      reset your password.
                    </p>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        required
                        value={forgotPasswordData.email}
                        onChange={(e) =>
                          handleForgotPasswordInputChange(
                            "email",
                            e.target.value
                          )
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        placeholder="Enter your email"
                        disabled={forgotPasswordLoading}
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: OTP Input */}
                {forgotPasswordStep === 2 && (
                  <div>
                    <p className="text-gray-600 mb-4">
                      We've sent an OTP to{" "}
                      <strong>{forgotPasswordData.email}</strong>. Please enter
                      the 4-digit code below.
                    </p>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OTP Code
                      </label>
                      <div className="flex gap-3 justify-center">
                        {forgotPasswordData.otp.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            maxLength="1"
                            value={digit}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                            disabled={forgotPasswordLoading}
                            autoComplete="off"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: New Password Input */}
                {forgotPasswordStep === 3 && (
                  <div>
                    <p className="text-gray-600 mb-4">
                      Enter your new password below.
                    </p>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          required
                          value={forgotPasswordData.newPassword}
                          onChange={(e) =>
                            handleForgotPasswordInputChange(
                              "newPassword",
                              e.target.value
                            )
                          }
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          placeholder="Enter new password (8+ characters)"
                          disabled={forgotPasswordLoading}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-400" />
                          ) : (
                            <Eye className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmNewPassword ? "text" : "password"}
                          required
                          value={forgotPasswordData.confirmPassword}
                          onChange={(e) =>
                            handleForgotPasswordInputChange(
                              "confirmPassword",
                              e.target.value
                            )
                          }
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                          placeholder="Confirm new password"
                          disabled={forgotPasswordLoading}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          onClick={() =>
                            setShowConfirmNewPassword(!showConfirmNewPassword)
                          }
                        >
                          {showConfirmNewPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-400" />
                          ) : (
                            <Eye className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                    disabled={forgotPasswordLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="flex-1 bg-primary-600 text-white py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgotPasswordLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {forgotPasswordStep === 1 && "Sending..."}
                        {forgotPasswordStep === 2 && "Verifying..."}
                        {forgotPasswordStep === 3 && "Updating..."}
                      </div>
                    ) : (
                      <>
                        {forgotPasswordStep === 1 && "Send OTP"}
                        {forgotPasswordStep === 2 && "Verify OTP"}
                        {forgotPasswordStep === 3 && "Update Password"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Login;
